import { reactive, ref, computed, onMounted, onBeforeUnmount } from 'vue'

import SchemaMarkupDropdown from './SchemaMarkupDropdown/SchemaMarkupDropdown.vue'
import SchemaField from './SchemaField/SchemaField.vue'

import parseSchemaToFields, { fieldsToSchema, fieldsForType } from './SchemaMarkup.mapping'
import { post } from '../../foundation/js/api'

export default {
    name: "SchemaMarkup",

    components: {
        SchemaMarkupDropdown,
        SchemaField
    },

    props: {
        schemaJson: {
            type: String,
            required: false,
            default: ""
        },
        contentId: {
            type: String,
            required: false,
            default: ""
        },
        pageUrl: {
            type: String,
            required: false,
            default: ""
        }
    },

    setup(props) {
        const schema = reactive(parseSchemaToFields(props.schemaJson))

        const nameField = computed(() => schema.fields.find((field) => field.key === "name") || null)
        const bodyFields = computed(() => schema.fields.filter((field) => field.key !== "name"))

        // Switching type in the dropdown re-renders the form to that type's fields
        // (empty where missing), keeping values for any fields that carry over.
        const onTypeChange = (newType) => {
            schema.type = newType
            schema.fields = fieldsForType(newType, schema.fields)
        }

        const save = () => {
            if (!props.contentId) {
                return
            }
            const schemaJson = JSON.stringify(fieldsToSchema(schema), null, 2)
            post("/api/schema-markup", JSON.stringify({
                contentLink: props.contentId,
                schemaJson
            }))
        }

        const isRestoring = ref(false)

        // Tell the parent shell (dialog chrome) to disable its buttons while busy.
        const setBusy = (value) => {
            if (window.parent && window.parent !== window) {
                window.parent.postMessage({ type: "kb.schemamarkup.busy", value }, window.location.origin)
            }
        }

        const restore = async () => {
            if (!props.pageUrl) {
                return
            }
            isRestoring.value = true
            setBusy(true)
            try {
                const res = await fetch(props.pageUrl, { credentials: "same-origin" })
                if (!res.ok) {
                    return
                }
                const html = await res.text()
                const doc = new DOMParser().parseFromString(html, "text/html")
                const region = doc.querySelector("#maincontent") ?? doc.body
                const pageText = (region?.textContent ?? "").replace(/\s+/g, " ").trim()
                // Send the page text + selected type to the backend, which converts it via AD.AI.
                const data = await post("/api/schema-markup/restore", JSON.stringify({
                    contentLink: props.contentId,
                    pageText,
                    type: schema.type
                }))
                // Repopulate the form from the returned schema.org JSON-LD.
                if (data?.schemaJson) {
                    const next = parseSchemaToFields(data.schemaJson)
                    schema.type = next.type
                    schema.typeOptions = next.typeOptions
                    schema.fields = next.fields
                }
            } finally {
                isRestoring.value = false
                setBusy(false)
            }
        }

        // No schema saved for this page yet - generate a first draft from the page's own content.
        onMounted(() => {
            if (!props.schemaJson) {
                restore()
            }
        })

        const onMessage = (event) => {
            if (event.origin !== window.location.origin) {
                return
            }
            if (event.data?.type === "kb.schemamarkup.save") {
                save()
            } else if (event.data?.type === "kb.schemamarkup.restore") {
                restore()
            }
        }
        window.addEventListener("message", onMessage)
        onBeforeUnmount(() => window.removeEventListener("message", onMessage))

        return { schema, nameField, bodyFields, save, restore, isRestoring, onTypeChange }
    }
}
